import { doc, getDoc } from "firebase/firestore";
import { isAllSeasons, normalizeSeasonToShort } from "./seasonFilter";

export const LEAGUE_TEAM_RANKINGS_CACHE_COLLECTION =
  "leagueTeamRankingsCache";

/**
 * Max age for using the server-built snapshot. Slightly over 24h so a daily
 * cron can run late without every client falling back to heavy live queries.
 */
export const TEAM_RANKINGS_CACHE_MAX_AGE_MS = 30 * 60 * 60 * 1000;

function timestampToMs(ts) {
  if (!ts) return null;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds != null) return Number(ts.seconds) * 1000;
  return null;
}

/**
 * Cache doc keys for seasons: "All" or normalized short label e.g. "2025-26".
 */
export function teamRankingsSeasonCacheKey(selectedSeason) {
  if (isAllSeasons(selectedSeason)) return "All";
  const n = normalizeSeasonToShort(String(selectedSeason || "").trim());
  return n && n !== "All" ? n : "All";
}

/**
 * @returns {{ teams: object[], seasonHint: string } | null}
 */
function pickFromCachePayload(data, seasonKey) {
  if (!data || typeof data !== "object") return null;
  const v = Number(data.payloadVersion) || 0;
  const bySeason = data.bySeason;

  if (v >= 2 && bySeason && typeof bySeason === "object") {
    const norm =
      seasonKey !== "All" ? normalizeSeasonToShort(seasonKey) : "All";
    const bucket =
      bySeason[seasonKey] ||
      (norm && norm !== seasonKey && bySeason[norm]);
    if (bucket == null) return null;
    if (!Array.isArray(bucket.teams)) return null;
    const seasonHint =
      typeof bucket.seasonHint === "string" ? bucket.seasonHint : "";
    return { teams: bucket.teams, seasonHint };
  }

  if (seasonKey === "All" && Array.isArray(data.teams)) {
    return { teams: data.teams, seasonHint: "" };
  }
  if (seasonKey !== "All") return null;
  return Array.isArray(data.teams)
    ? { teams: data.teams, seasonHint: "" }
    : null;
}

/**
 * @param {import("firebase/firestore").Firestore} db
 * @param {string} leagueId
 * @param {string} selectedSeason Same as season toolbar ("All" or e.g. "2025-26")
 * @returns {Promise<{ teams: object[], updatedLabel: string, seasonHint: string } | null>}
 */
export async function loadFreshTeamRankingsFromCache(
  db,
  leagueId,
  selectedSeason
) {
  if (!leagueId || !db) return null;
  try {
    const ref = doc(db, LEAGUE_TEAM_RANKINGS_CACHE_COLLECTION, leagueId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    const ms = timestampToMs(data.updatedAt);
    if (ms == null) return null;
    const age = Date.now() - ms;
    if (age < 0 || age > TEAM_RANKINGS_CACHE_MAX_AGE_MS) return null;

    const seasonKey = teamRankingsSeasonCacheKey(selectedSeason);
    const picked = pickFromCachePayload(data, seasonKey);
    if (picked == null) return null;
    const { teams, seasonHint } = picked;
    /* Empty snapshot: fall back to live aggregation. */
    if (!teams.length) return null;

    const d = new Date(ms);
    const updatedLabel = `Daily snapshot · updated ${d.toLocaleString(
      undefined,
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    )}`;

    return { teams, updatedLabel, seasonHint };
  } catch (_e) {
    /* Permission denied / offline: do not let this skip live team aggregation. */
    return null;
  }
}
